/**
 * checkValidity - Checks the validity of form fields on submit
 * @param {event} e
 * @param {function} callback
 */
export function checkValidity(e, callback) {
  e.preventDefault();

  const form = e.target;
  const invalidFields = form.querySelectorAll(
    'input[required]:invalid, select[required]:invalid, textarea[required]:invalid, input[required][aria-invalid="true"]'
  );

  // Mark the form as submitted
  form.className += ' form--submitted';

  // Remove any existing error messages
  const existingErrors = form.querySelectorAll('.form__err');

  for (let i = 0; i < existingErrors.length; i++) {
    existingErrors[i].remove();
  }

  // Handle validation
  if (invalidFields.length > 0) {
    // If there are invalid fields, insert error messages
    for (let i = 0; i < invalidFields.length; i++) {
      const errorMsg = invalidFields[i].getAttribute('data-errormsg');
      const errorEl = document.createElement('label');

      errorEl.innerHTML = errorMsg;
      errorEl.className = 'form__err';
      errorEl.setAttribute('for', invalidFields[i].id);
      errorEl.setAttribute('role', 'alert');

      // Insert error labels (for styled select elements, use the grandparent)
      const parent = invalidFields[i].parentNode;

      if (parent.classList.contains('select')) {
        const grandParent = parent.parentNode;
        grandParent.insertBefore(errorEl, invalidFields[i].nextSibling);
      } else if (parent.classList.contains('file')) {
        const grandParent = parent.parentNode;
        grandParent.insertBefore(errorEl, parent.nextSibling);
      } else {
        parent.insertBefore(errorEl, invalidFields[i].nextSibling);
      }
    }
  } else {
    if (callback) {
      callback(e);
    }
  }
}

/**
 * deformatQuery - Takes a URL, extracts the query string and returns it as an object
 * @param {url} query
 */
export function deformatQuery(url) {
  const queryString = url.split('?')[1];
  const queryObj = JSON.parse(
    `{ "${queryString.replace(/&/g, '","').replace(/=/g, '":"')}" }`,
    (key, value) => {
      return key === '' ? value : decodeURIComponent(value);
    }
  );

  return queryObj;
}

export function aceEditorLanguageSelection(language){

  let languageObject = {
    cs: 'csharp',
    cpp: 'c_cpp',
    apache: 'apache_conf',
    coffeescript: 'coffee'
  }
  if(languageObject[language] ){
    return languageObject[language]
  } else {
    return language;
  }

}

/**
 * formatQuery - Takes an object and returns it as URL query parameters
 * @param {object} query
 */
export function formatQuery(query) {
  let queryString = '';

  for (let key in query) {
    // Check to make sure the value in the key is defined
    if(query[key]) {
      if (!queryString) {
        // If this is the first key, add a ?
        queryString += '?';
      } else {
        // Otherwise, add a &
        queryString += '&';
      }

      queryString += `${key}=${query[key]}`;
    }
  }

  return queryString;
}

/**
 * mergeDeep - Recursively merges two objects.
 * @param {object} target
 * @param {object} source
 */
export function mergeDeep(target, source) {
  let output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target))
          Object.assign(output, { [key]: source[key] });
        else
          output[key] = mergeDeep(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

/**
 * getData - Gets data out of sessionStorage and falls back to cookies
 * @param {string} name
 */
export function getData(name) {
  try {
    return sessionStorage.getItem(name);
  } catch (error) {
    const match = document.cookie.match(new RegExp(name + '=([^;]+)'));

    if (match) {
      return match[1];
    } else {
      return null;
    }
  }
}

/**
 * getLocalData - Gets data out of localStorage and falls back to cookies
 * @param {string} name
 */
export function getLocalData(name) {
  try {
    return localStorage.getItem(name);
  } catch (error) {
    const match = document.cookie.match(new RegExp(name + '=([^;]+)'));

    if (match) {
      return match[1];
    } else {
      return null;
    }
  }
}

/**
 * getParam - Gets query params from a string
 * @export
 * @param {string} name
 * @param {string} url
 */
export function getParam(name, url) {
  if (!url) url = window.location.search;

  name = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
  let results = regex.exec(url);

  if (!results) {
    return null;
  }

  if (!results[2]) {
    return '';
  }

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

/**
 * getUrlSegment - Gets a given URL segment from a string
 * @param {integer} segment
 * @param {string} pathname
 */
export function getUrlSegment(segment, pathname) {
  if (!pathname) pathname = window.location.pathname;

  let pathArray = pathname.split('/');

  if (pathArray[0] === '') {
    pathArray.splice(0, 1);
  }

  return pathArray[segment - 1];
}

/**
 * isDescendant - Checks if an element is a descendant of a given parent
 * @export
 * @param {parent} element
 * @param {child} element
 * @return boolean
 */
export function isDescendant(parent, child) {
  let node = child.parentNode;

  while (node !== null) {
    if (node === parent) {
      return true;
    }

    node = node.parentNode;
  }

  return false;
}

/**
 * isObject - Tests if the parameter is an object.
 */
export function isObject(item) {
  return (item && typeof item === 'object' && !Array.isArray(item));
}

/**
 * isEmptyObject - Tests if the parameter is an empty object.
 */
export function isEmptyObject(object) {
  if (isObject(object)) {
    for (let item in object) {
      return false;
    }
    return true;
  }

  return false;
}

/**
 * loadCSS - Loads a CSS file
 */
export function loadCSS(name, src) {
  const css = document.createElement('link');
  css.id = `${name}-css`;
  css.type = 'text/css';
  css.rel = 'stylesheet';
  css.href = src;

  document.head.append(css);
}

/**
 * loadJS - Loads a JS file
 */
export function loadJS(script, callback) {
  const js = document.createElement('script');
  js.id = `${script.name}-js`;
  js.type = 'text/javascript';
  js.async = true;
  js.src = script.src;

  if (callback) {
    js.onload = () => callback(script);
  }

  document.body.appendChild(js);
}

/**
 * parseMinutes - Takes minutes and returns it as hh:mm
 */
export function parseMinutes(minutes) {
  return new Date(minutes * 60000).toISOString().substr(11, 5);
}

/**
 * parseSeconds - Takes seconds and returns it as mm:ss or #m #s
 * @param {string} seconds
 * @param {boolean} durationString
 */
export function parseSeconds(seconds, durationString) {
  let time = new Date(seconds * 1000).toISOString().substr(14, 5);

  if (durationString) {
    time = time.replace(':', 'm ');
    time += 's';
  }

  return time;
}

/**
 * readableMinutes - Takes minutes and returns it as # hours and # minutes
 */
export function readableMinutes(minutes, labels) {
  const hr = Math.round(minutes / 60);
  let lbls = labels;

  if (!labels) {
    lbls = {
      hour: 'hour',
      hours: 'hours',
      minute: 'minute',
      minutes: 'minutes'
    };
  }

  let hrLabel = lbls.hours;

  if (hr === 1) {
    hrLabel = lbls.hour;
  }

  const min = minutes % 60;
  let minLabel = lbls.minutes;

  if (min === 1) {
    minLabel = lbls.minute;
  }

  let string = '';

  if (hr) {
    string = string + `${hr} ${hrLabel}`;
  }

  if (min) {
    string = string + `${hr ? ' ' : ''}${min} ${minLabel}`;
  }

  return string;
}

/**
 * storeData - Stores data in sessionStorage and falls back to cookies
 * @param {string} name
 * @param {string} data
 */
export function storeData(name, data) {
  try {
    sessionStorage.setItem(name, data);
  } catch (error) {
    document.cookie = name + '=' + data + '; expires=0; path=/';
  }
}

/**
 * storeLocalData - Stores data in localStorage and falls back to cookies
 * @param {string} name
 * @param {string} data
 */
export function storeLocalData(name, data) {
  try {
    localStorage.setItem(name, data);
  } catch (error) {
    document.cookie = name + '=' + data + '; expires=0; path=/';
  }
}
